import { fixedUi } from '@/i18n/fixedUi';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import SheinOpsPager, { SHEIN_OPS_DEFAULT_PAGE_SIZE } from '@/shein-platform/components/SheinOpsPager';
import SheinOpsListToolbar, { downloadCsv } from '@/shein-platform/components/SheinOpsListToolbar';
import { ProductFilterCard, ProductListCard } from '@/shein-platform/modules/products/components/ProductPageCards';
import HoverCopyField from '@/shein-platform/modules/products/components/HoverCopyField';
import SheinProductThumb from '@/shein-platform/modules/products/components/SheinProductThumb';
import SheinMarketingActivityDrawer from '@/shein-platform/modules/ops/SheinMarketingActivityDrawer';
import SheinMarketingSkuPriceModal from '@/shein-platform/modules/ops/SheinMarketingSkuPriceModal';
import {
  useSheinPageShopScope,
  stackShopDimensionTabs,
} from '@/shein-platform/hooks/useSheinPageShopScope';
import { useGuardedRefresh } from '@/shein-platform/hooks/useGuardedRefresh';
import SheinSyncProgressBar from '@/shein-platform/components/SheinSyncProgressBar';
import SheinSyncProgressSlot from '@/shein-platform/components/SheinSyncProgressSlot';
import { useSheinSyncJobs } from '@/shein-platform/context/SheinSyncJobContext';
import { sheinPlatformAPI } from '@/shein-platform/api/sheinPlatformApi';
import { formatSiteListSyncedAt } from '@/shein-platform/utils/sheinSiteList';
import {
  buildSheinPath,
  normalizeSheinShopTypeSlug,
  openSheinPathInNewTab,
} from '@/shein-platform/constants/sheinRoutes';
import { buildOpsExportMenu, opsExportLabel } from '@/shein-platform/utils/buildOpsExportMenu';
import { fetchAllPages } from '@/shein-platform/utils/sheinExportHelpers';
import { togglePageSelection } from '@/shein-platform/utils/sheinPageSelection';
import { showSheinConfirm } from '@/shein-platform/components/SheinConfirmDialog';
import {
  ENROLL_SKU_PREVIEW_LIMIT,
  activityTypeLabel,
  auditStatusLabel,
  auditStatusTone,
  canCancelEnroll,
  enrollProductInfo,
  enrollSkuPriceRows,
  formatMsTime,
  formatTimeRange,
  groupEnrollRowsBySpu,
} from '@/shein-platform/utils/sheinMarketingDisplay';
import { showToast } from '@/utils/toast';
import '@/styles/shein-platform/shein-product-ops.css';

const ACTIVITY_TYPE_TABS = [
  { value: '', label: '全部', labelKey: 'tiktokCatalog.all' },
  { value: '1', label: '频道', labelKey: 'marketing.sht1' },
  { value: '2', label: '清仓', labelKey: 'marketing.sht2' },
  { value: '3', label: '大促', labelKey: 'marketing.sht3' },
  { value: '4', label: '日常', labelKey: 'marketing.sht4' },
];

const AUDIT_STATUS_TABS = [
  { value: '', label: '全部', labelKey: 'tiktokCatalog.all' },
  { value: '0', label: '审核中', labelKey: 'marketing.sha0' },
  { value: '1', label: '审核通过', labelKey: 'marketing.sha1' },
  { value: '2', label: '审核不通过', labelKey: 'marketing.sha2' },
  { value: '4', label: '取消报名', labelKey: 'marketing.sha4' },
  { value: '7', label: '活动暂停', labelKey: 'marketing.sha7' },
  { value: '8', label: '取消中', labelKey: 'marketing.sha8' },
];

const VIEW_META = {
  activities: {
    title: '活动列表',
    titleKey: 'marketing.actList',
    syncKinds: ['marketing'],
    quotaKind: 'marketing',
    colSpan: 7,
    syncHint: '列表读本地缓存；点击「拉取 SHEIN 后台数据」获取平台最新可报名活动。',
    syncHintKey: 'marketing.sheinSyncAct',
    emptyNever: '尚未同步可报名活动',
    emptyNeverKey: 'marketing.neverAct',
    emptyNone: '暂无可报名活动',
    emptyNoneKey: 'marketing.noneAct',
    loadingLabel: '正在查询活动…',
    loadingLabelKey: 'marketing.queryAct',
    keywordPlaceholder: '活动名称 / 活动 ID',
    keywordPlaceholderKey: 'marketing.sheinKwAct',
    selectAllLabelKey: 'marketing.selectAct',
    pagerLabelKey: 'marketing.pagerAct',
    exportFilePrefix: 'shein-marketing-activities',
    selectAllLabel: '全选活动',
    pagerLabel: '活动列表分页',
  },
  enrolls: {
    title: '报名记录',
    titleKey: 'marketing.enrollList',
    syncKinds: ['marketing-enrolls'],
    quotaKind: 'marketing-enrolls',
    colSpan: 9,
    syncHint: '列表读本地缓存；点击「拉取 SHEIN 后台数据」只拉取平台报名记录（不会拉取活动列表）。本页仅查看/取消，报名请到「活动列表」。',
    syncHintKey: 'marketing.sheinSyncEnroll',
    emptyNever: '尚未同步报名记录',
    emptyNeverKey: 'marketing.neverEnroll',
    emptyNone: '暂无报名记录',
    emptyNoneKey: 'marketing.noneEnroll',
    loadingLabel: '正在查询报名记录…',
    loadingLabelKey: 'marketing.queryEnroll',
    keywordPlaceholder: 'SKC / 活动 ID',
    keywordPlaceholderKey: 'marketing.sheinKwEnroll',
    selectAllLabelKey: 'marketing.selectEnroll',
    pagerLabelKey: 'marketing.pagerEnroll',
    exportFilePrefix: 'shein-marketing-enrolls',
    selectAllLabel: '全选报名记录',
    pagerLabel: '报名记录分页',
  },
};

function extractListPayload(res) {
  if (!res || typeof res !== 'object') {
    return {
      items: [], total: 0, typeCounts: { all: 0 }, statusCounts: { all: 0 }, truncated: false,
      neverSynced: true, lastSyncedAt: null,
    };
  }
  const nested = res.info && typeof res.info === 'object' ? res.info : res;
  const items = Array.isArray(nested.items)
    ? nested.items
    : (Array.isArray(nested.activityList)
      ? nested.activityList
      : (Array.isArray(nested.goodsInfoList) ? nested.goodsInfoList : []));
  const total = Number.isFinite(Number(nested.total))
    ? Number(nested.total)
    : (Number.isFinite(Number(nested.count)) ? Number(nested.count) : items.length);
  const typeCounts = nested.typeCounts && typeof nested.typeCounts === 'object'
    ? nested.typeCounts
    : { all: total };
  const statusCounts = nested.statusCounts && typeof nested.statusCounts === 'object'
    ? nested.statusCounts
    : { all: total };
  return {
    items,
    total,
    typeCounts,
    statusCounts,
    truncated: Boolean(nested.truncated),
    // 后端缓存状态：neverSynced=本地从未同步过，用于区分「尚未同步」与「确实没有数据」
    neverSynced: nested.neverSynced !== false,
    lastSyncedAt: nested.lastSyncedAt || null,
    // 活动列表模块是否同步过（报名记录页据此提示用户先去活动列表同步）；缺省按已同步处理，避免误提示
    activitiesSynced: nested.activitiesSynced !== false,
  };
}

function rowKey(row, view, index = 0) {
  if (view === 'enrolls') {
    return String(row?.partakeGoodsId || row?.id || `${row?.skc || 'enroll'}-${index}`);
  }
  return String(row?.activityId || row?.id || `activity-${index}`);
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** 取消报名任务轮询间隔（任务在 MQ Worker 上跑，轮询只为刷新进度条） */
const CANCEL_JOB_POLL_MS = 700;
/** 轮询连续失败上限（含网络抖动） */
const CANCEL_JOB_MAX_POLL_ERRORS = 5;

function summarizeCancelJob(view) {
  const successCount = Number(view?.successCount);
  const failCount = Number(view?.failCount);
  const okSuccess = Number.isFinite(successCount) ? successCount : 0;
  const okFail = Number.isFinite(failCount) ? failCount : 0;
  const message = String(view?.message || view?.error || '').trim()
    || (okFail > 0 && okSuccess === 0
      ? `取消失败：成功 0，失败 ${okFail}`
      : `取消完成：成功 ${okSuccess}，失败 ${okFail}`);
  return { successCount: okSuccess, failCount: okFail, message };
}

/**
 * SHEIN 营销活动列表：活动列表 / 报名记录（布局对齐 Temu 营销页）
 */
export default function SheinMarketingPage({ view = 'activities' }) {
  const { t } = useTranslation('workbench');
  const pageView = VIEW_META[view] ? view : 'activities';
  const meta = VIEW_META[pageView];
  const { shopType } = useParams();
  const slug = normalizeSheinShopTypeSlug(shopType) || 'semi';
  const { shopDimensionTabs, concreteScopeId } = useSheinPageShopScope();
  const shopId = concreteScopeId || '';
  const syncKind = meta.syncKinds[0];
  const { startJob, onTrackDone, isBusy } = useSheinSyncJobs();

  const [filterDraft, setFilterDraft] = useState({ keyword: '', auditStatus: '' });
  const [applied, setApplied] = useState({ keyword: '', activityType: '', auditStatus: '' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(SHEIN_OPS_DEFAULT_PAGE_SIZE);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [typeCounts, setTypeCounts] = useState({ all: 0 });
  const [statusCounts, setStatusCounts] = useState({ all: 0 });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [truncated, setTruncated] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  /** 后端缓存状态：本地从未同步过 → 空列表提示「尚未同步」而不是「暂无数据」 */
  const [neverSynced, setNeverSynced] = useState(true);
  /** 活动列表模块是否同步过：报名记录页在未同步过活动列表时提示用户先去活动列表 */
  const [activitiesSynced, setActivitiesSynced] = useState(true);
  const [drawerActivity, setDrawerActivity] = useState(null);
  const [skuModal, setSkuModal] = useState(null);
  const [deletingId, setDeletingId] = useState('');
  const [batchDeleting, setBatchDeleting] = useState(false);
  /**
   * 进行中的取消任务：jobId → 进度视图。
   * 允许多任务并行；仅锁定已进入任务的报名记录，其它记录仍可继续取消。
   */
  const [cancelJobs, setCancelJobs] = useState(() => ({}));
  /** 已进入异步取消任务的 partakeGoodsId（state 驱动 UI，ref 防并发双点） */
  const [pendingCancelIds, setPendingCancelIds] = useState(() => new Set());
  const pendingCancelIdsRef = useRef(new Set());
  const cancelAliveRef = useRef(true);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [selectedRowsMap, setSelectedRowsMap] = useState(() => ({}));

  useEffect(() => () => {
    cancelAliveRef.current = false;
  }, []);

  const openEnrollInNewTab = useCallback((row) => {
    if (!shopId) {
      showToast(fixedUi("请先选择 SHEIN 店铺"), 'warning');
      return;
    }
    const activityId = row?.activityId;
    if (activityId == null || activityId === '') {
      showToast(fixedUi("活动 ID 无效"), 'warning');
      return;
    }
    const q = new URLSearchParams();
    q.set('shopId', String(shopId));
    if (row.activityName) q.set('name', String(row.activityName));
    if (row.activityType != null) q.set('activityType', String(row.activityType));
    const path = `${buildSheinPath(slug, `marketing/enroll/${activityId}`)}?${q.toString()}`;
    openSheinPathInNewTab(path);
  }, [shopId, slug]);

  const buildListBody = useCallback((filters, pageNo, size) => {
    const body = {
      pageNum: Math.max(1, Number(pageNo) || 1),
      pageSize: Math.max(1, Number(size) || SHEIN_OPS_DEFAULT_PAGE_SIZE),
    };
    const kw = String(filters.keyword || '').trim();
    if (pageView === 'activities') {
      if (filters.activityType) {
        body.activityTypeList = [Number(filters.activityType)];
      }
      if (kw) {
        if (/^\d+$/.test(kw)) body.activityIdList = [Number(kw)];
        else body.keyword = kw;
      }
    } else if (kw) {
      if (/^\d+$/.test(kw)) body.activityIdList = [Number(kw)];
      else body.skcList = [kw];
    }
    // 审核状态平台不支持筛选，交由后端聚合后本地过滤
    if (pageView === 'enrolls'
      && filters.auditStatus !== '' && filters.auditStatus != null) {
      body.auditStatus = String(filters.auditStatus);
    }
    return body;
  }, [pageView]);

  const applyListPayload = useCallback((res) => {
    // 关键词 / 审核状态过滤与 Tab 计数已由后端在全量缓存上完成，前端不再按当前页二次统计
    const payload = extractListPayload(res);
    setRows(payload.items);
    setTotal(payload.total);
    setTypeCounts(payload.typeCounts);
    setStatusCounts(payload.statusCounts);
    setTruncated(payload.truncated);
    setNeverSynced(payload.neverSynced);
    setActivitiesSynced(payload.activitiesSynced);
    setLastSyncedAt(payload.lastSyncedAt);
    setSelectedKeys(new Set());
    setSelectedRowsMap({});
  }, []);

  const loadList = useCallback(async (sid, filters, currentView, pageNo, size) => {
    if (!sid) {
      applyListPayload(null);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const body = buildListBody(filters, pageNo, size);
      const res = currentView === 'enrolls'
        ? await sheinPlatformAPI.listMarketingEnrolls(sid, body)
        : await sheinPlatformAPI.listMarketing(sid, body);
      applyListPayload(res);
    } catch (e) {
      applyListPayload(null);
      setError(e?.message || t('marketing.loadFail', { title: t(meta.titleKey) }));
      showToast(e?.message || t('marketing.loadFail', { title: t(meta.titleKey) }), 'error');
    } finally {
      setLoading(false);
    }
  }, [buildListBody, applyListPayload, meta.titleKey, t]);

  useEffect(() => {
    setPage(1);
    setFilterDraft({ keyword: '', auditStatus: '' });
    setApplied({ keyword: '', activityType: '', auditStatus: '' });
    setSelectedKeys(new Set());
    setSelectedRowsMap({});
    setRows([]);
    setTotal(0);
    setTypeCounts({ all: 0 });
    setStatusCounts({ all: 0 });
    setTruncated(false);
    setError('');
    setLastSyncedAt(null);
    setNeverSynced(true);
    setSkuModal(null);
  }, [shopId, pageView]);

  // 进页读本地缓存（不打平台）；筛选/翻页同样只查本地库
  useEffect(() => {
    if (!shopId) return undefined;
    loadList(shopId, applied, pageView, page, pageSize);
    return undefined;
  }, [shopId, applied, pageView, page, pageSize, loadList]);

  const handleSync = useCallback(async () => {
    if (!shopId) {
      showToast(fixedUi("请先选择 SHEIN 店铺"), 'warning');
      return;
    }
    // 工具栏「拉取 SHEIN 后台数据」：异步 Job（真进度）；同步期间仍展示本地缓存
    try {
      const track = await startJob({
        kind: syncKind,
        shopId,
        label: t('marketing.syncNamed', { title: t(meta.titleKey) }),
      });
      if (!track?.id) return;
      const result = await new Promise((resolve) => {
        const unsub = onTrackDone(track.id, (r) => {
          unsub();
          resolve(r);
        });
      });
      if (!result?.ok) return;
      await loadList(shopId, applied, pageView, page, pageSize);
    } catch (e) {
      if (e?.code === 'SYNC_IN_PROGRESS' || e?.code === 'ABORT') {
        return;
      }
      // startJob 已 toast；此处吞掉避免 unhandled
    }
  }, [
    shopId,
    pageView,
    applied,
    page,
    pageSize,
    syncKind,
    startJob,
    onTrackDone,
    loadList,
    meta.titleKey,
    t,
  ]);

  const {
    refresh: onSync,
    refreshing: syncingLocal,
    cooldownSeconds: syncCooldown,
  } = useGuardedRefresh(handleSync, { minIntervalMs: 5000 });

  const syncing = syncingLocal || isBusy(syncKind, shopId);

  const lastSyncedLabel = useMemo(() => {
    if (syncing || loading) return t('productAction.syncing');
    if (!lastSyncedAt) return t('marketing.neverPullShein');
    try {
      return t('marketing.syncedAt', { time: formatSiteListSyncedAt(lastSyncedAt) });
    } catch {
      return t('marketing.syncedAt', { time: String(lastSyncedAt) });
    }
  }, [syncing, loading, lastSyncedAt, t]);

  const onQuery = () => {
    setPage(1);
    setApplied((prev) => ({
      ...prev,
      keyword: String(filterDraft.keyword || '').trim(),
      auditStatus: String(filterDraft.auditStatus || ''),
    }));
  };

  const onReset = () => {
    setFilterDraft({ keyword: '', auditStatus: '' });
    setPage(1);
    setApplied({ keyword: '', activityType: '', auditStatus: '' });
  };

  const onActivityTypeTab = (value) => {
    setPage(1);
    setApplied((prev) => ({
      ...prev,
      activityType: String(value || ''),
    }));
  };

  const onAuditStatusTab = (value) => {
    setPage(1);
    setFilterDraft((prev) => ({ ...prev, auditStatus: String(value || '') }));
    setApplied((prev) => ({
      ...prev,
      auditStatus: String(value || ''),
    }));
  };

  const selectableKeys = useMemo(
    () => rows.map((row, index) => rowKey(row, pageView, index)),
    [rows, pageView],
  );
  const allSelected = selectableKeys.length > 0
    && selectableKeys.every((k) => selectedKeys.has(k));

  const toggleAll = () => {
    setSelectedKeys((prev) => togglePageSelection(prev, selectableKeys, allSelected));
    setSelectedRowsMap((prev) => {
      const map = { ...prev };
      if (allSelected) {
        selectableKeys.forEach((k) => { delete map[k]; });
      } else {
        rows.forEach((row, index) => {
          map[rowKey(row, pageView, index)] = row;
        });
      }
      return map;
    });
  };

  const toggleOne = (key, row) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    setSelectedRowsMap((prev) => {
      const next = { ...prev };
      if (next[key]) delete next[key];
      else next[key] = row;
      return next;
    });
  };

  const onRowClickSelect = (e, key, row) => {
    if (e.target.closest('button, a, input, textarea, select, label, [role="button"]')) return;
    toggleOne(key, row);
  };

  /** 打开某条报名记录的完整 SKU 价格弹窗（头部展示 SPU / SKC / 货号，不展示商品标题） */
  const openSkuModal = useCallback((group, info, skuRows) => {
    setSkuModal({
      spu: info?.spu || group?.product?.spu || '',
      skc: info?.skc || group?.product?.skc || '',
      supplierCode: info?.supplierCode || group?.product?.supplierCode || '',
      rows: skuRows,
    });
  }, []);

  const resolveSelectedRows = () => {
    const list = [];
    selectedKeys.forEach((k) => {
      if (selectedRowsMap[k]) list.push(selectedRowsMap[k]);
    });
    return list;
  };

  const exportRows = useCallback((source) => {
    if (!source.length) {
      showToast(fixedUi("暂无可导出数据"), 'warning');
      return;
    }
    if (pageView === 'enrolls') {
      downloadCsv(
        `${meta.exportFilePrefix}-${Date.now()}.csv`,
        ['partakeGoodsId', 'title', 'supplierCode', 'spu', 'skc', 'color', 'activityId', 'auditStatus', 'partakeTime', 'skuPriceDetail'],
        source.map((row) => {
          const info = enrollProductInfo(row);
          return {
            partakeGoodsId: row.partakeGoodsId ?? '',
            title: info.title,
            supplierCode: info.supplierCode,
            spu: info.spu,
            skc: info.skc,
            color: info.color,
            activityId: row.activityId ?? '',
            auditStatus: auditStatusLabel(row.auditStatus),
            partakeTime: formatMsTime(row.partakeTime),
            skuPriceDetail: enrollSkuPriceRows(row)
              .map((sku) => `${sku.sku}: ${sku.priceText}`)
              .join(' | '),
          };
        }),
      );
    } else {
      downloadCsv(
        `${meta.exportFilePrefix}-${Date.now()}.csv`,
        ['activityId', 'activityName', 'activityType', 'partakeTime', 'activityTime'],
        source.map((row) => ({
          activityId: row.activityId ?? '',
          activityName: row.activityName ?? '',
          activityType: activityTypeLabel(row.activityType),
          partakeTime: formatTimeRange(row.partakeStartTime, row.partakeEndTime),
          activityTime: formatTimeRange(row.activityStartTime, row.activityEndTime),
        })),
      );
    }
    showToast(`已导出 ${source.length} 条`, 'success');
  }, [meta.exportFilePrefix, pageView]);

  const exportFiltered = useCallback(async () => {
    if (!shopId) {
      showToast(fixedUi("请先选择 SHEIN 店铺"), 'warning');
      return;
    }
    if (exporting) return;
    setExporting(true);
    try {
      const filters = applied;
      const currentView = pageView;
      const all = await fetchAllPages(async (pageNo, size) => {
        const body = buildListBody(filters, pageNo, size);
        const res = currentView === 'enrolls'
          ? await sheinPlatformAPI.listMarketingEnrolls(shopId, body)
          : await sheinPlatformAPI.listMarketing(shopId, body);
        const payload = extractListPayload(res);
        return { items: payload.items, total: payload.total };
      });
      exportRows(all);
    } catch (e) {
      showToast(e?.message || fixedUi("导出筛选失败"), 'error');
    } finally {
      setExporting(false);
    }
  }, [shopId, exporting, applied, pageView, buildListBody, exportRows]);

  /** 删除选中项（本地缓存行已不存在，勾选状态同步清掉） */
  const dropSelection = useCallback((keys) => {
    if (!keys || !keys.length) return;
    const drop = new Set(keys);
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      drop.forEach((k) => next.delete(k));
      return next;
    });
    setSelectedRowsMap((prev) => {
      const next = { ...prev };
      drop.forEach((k) => { delete next[k]; });
      return next;
    });
  }, []);

  const releasePendingCancelIds = useCallback((ids) => {
    if (!ids?.length) return;
    ids.forEach((id) => pendingCancelIdsRef.current.delete(String(id)));
    setPendingCancelIds(new Set(pendingCancelIdsRef.current));
  }, []);

  const claimPendingCancelIds = useCallback((ids) => {
    if (!ids?.length) return false;
    const keys = ids.map(String);
    const overlap = keys.filter((k) => pendingCancelIdsRef.current.has(k));
    if (overlap.length) {
      showToast(
        `以下报名已在取消任务中，请勿重复提交：${overlap.slice(0, 5).join('、')}${overlap.length > 5 ? '…' : ''}`,
        'warning',
      );
      return false;
    }
    keys.forEach((k) => pendingCancelIdsRef.current.add(k));
    setPendingCancelIds(new Set(pendingCancelIdsRef.current));
    return true;
  }, []);

  /** 轮询单个取消任务；页面卸载后返回 null（不影响其它并行任务） */
  const pollCancelJob = useCallback(async (jobId) => {
    let errors = 0;
    for (;;) {
      // eslint-disable-next-line no-await-in-loop
      await sleep(CANCEL_JOB_POLL_MS);
      if (!cancelAliveRef.current) return null;
      let view;
      try {
        // eslint-disable-next-line no-await-in-loop
        view = await sheinPlatformAPI.getMarketingEnrollCancelJob(shopId, jobId);
        errors = 0;
      } catch (e) {
        errors += 1;
        if (errors >= CANCEL_JOB_MAX_POLL_ERRORS) throw e;
        // eslint-disable-next-line no-continue
        continue;
      }
      if (!cancelAliveRef.current) return null;
      setCancelJobs((prev) => ({
        ...prev,
        [jobId]: { ...(view || {}), jobId, visible: true },
      }));
      if (view?.done) return view;
    }
  }, [shopId]);

  /**
   * 提交后台取消任务：可与其它任务并行；仅锁定本次报名记录。
   * HTTP 只拿到 jobId + queued，实际由 MQ Worker（无 MQ 时本机池）执行。
   */
  const startCancelJob = useCallback(async (targets, { clearSelection = false } = {}) => {
    const rows = (Array.isArray(targets) ? targets : []).filter(Boolean);
    if (!rows.length) return;

    const idKeys = rows
      .map((row) => (row?.partakeGoodsId == null || row.partakeGoodsId === ''
        ? ''
        : String(row.partakeGoodsId)))
      .filter(Boolean);
    const freeRows = rows.filter((row) => {
      const id = row?.partakeGoodsId == null ? '' : String(row.partakeGoodsId);
      return id && !pendingCancelIdsRef.current.has(id);
    });
    const skipped = idKeys.length - freeRows.length;
    if (!freeRows.length) {
      showToast(fixedUi("所选记录均已在取消任务中，请等待完成后再试"), 'warning');
      return;
    }
    if (skipped > 0) {
      showToast(`已跳过 ${skipped} 条进行中的记录，将取消其余 ${freeRows.length} 条`, 'warning');
    }

    const partakeGoodsIdList = freeRows.map((row) => (
      Number.isFinite(Number(row.partakeGoodsId)) ? Number(row.partakeGoodsId) : row.partakeGoodsId
    )).filter((v) => v != null && v !== '');
    if (!partakeGoodsIdList.length) {
      showToast(fixedUi("报名记录 ID 无效"), 'warning');
      return;
    }
    if (partakeGoodsIdList.length > 100) {
      showToast(fixedUi("单次最多取消 100 条，请分批提交"), 'warning');
      return;
    }

    const lockKeys = partakeGoodsIdList.map(String);
    if (!claimPendingCancelIds(lockKeys)) return;

    let jobId = '';
    try {
      const started = await sheinPlatformAPI.cancelMarketingEnroll(shopId, { partakeGoodsIdList });
      jobId = String(started?.jobId || '').trim();
      if (!jobId) throw new Error('未返回取消任务 ID，请稍后重试');

      setCancelJobs((prev) => ({
        ...prev,
        [jobId]: {
          ...(started || {}),
          jobId,
          visible: true,
          status: started?.status || 'queued',
          percent: Number(started?.percent) || 2,
          message: started?.message || '排队中…',
        },
      }));
      showToast(
        `已提交后台取消（${partakeGoodsIdList.length} 条），可继续取消其它记录`,
        'success',
      );

      const finalView = started?.done ? started : await pollCancelJob(jobId);
      if (!finalView || !cancelAliveRef.current) return;

      const summary = summarizeCancelJob(finalView);
      const tone = summary.failCount > 0 && summary.successCount === 0
        ? 'error'
        : (summary.failCount > 0 ? 'warning' : 'success');
      showToast(summary.message, tone);

      const doneJobId = jobId;
      setTimeout(() => {
        if (!cancelAliveRef.current) return;
        setCancelJobs((prev) => {
          if (!prev[doneJobId]) return prev;
          const next = { ...prev };
          delete next[doneJobId];
          return next;
        });
      }, 3000);

      if (summary.successCount > 0) {
        if (clearSelection) {
          const drop = new Set(lockKeys);
          setSelectedKeys((prev) => {
            const next = new Set(prev);
            // 勾选 key 未必等于 partakeGoodsId，尽量清掉本次相关勾选
            drop.forEach((id) => next.delete(id));
            return next;
          });
          setSelectedRowsMap((prev) => {
            const next = { ...prev };
            Object.keys(next).forEach((key) => {
              const row = next[key];
              if (row && drop.has(String(row.partakeGoodsId))) delete next[key];
            });
            return next;
          });
        }
        await loadList(shopId, applied, pageView, page, pageSize);
      }
    } catch (e) {
      showToast(e?.message || '取消失败', 'error');
      if (jobId) {
        setCancelJobs((prev) => {
          if (!prev[jobId]) return prev;
          const next = { ...prev };
          delete next[jobId];
          return next;
        });
      }
    } finally {
      releasePendingCancelIds(lockKeys);
    }
  }, [
    shopId,
    applied,
    pageView,
    page,
    pageSize,
    loadList,
    pollCancelJob,
    claimPendingCancelIds,
    releasePendingCancelIds,
  ]);

  const cancelEnroll = useCallback(async (row) => {
    const partakeGoodsId = row?.partakeGoodsId;
    if (partakeGoodsId == null || partakeGoodsId === '') {
      showToast(fixedUi("报名记录 ID 无效"), 'warning');
      return;
    }
    if (!canCancelEnroll(row)) {
      showToast(fixedUi("仅审核中的报名可取消"), 'warning');
      return;
    }
    if (pendingCancelIdsRef.current.has(String(partakeGoodsId))) {
      showToast(fixedUi("该报名已在取消任务中，请等待完成"), 'warning');
      return;
    }
    const ok = await showSheinConfirm({
      title: '取消报名',
      message: `确认取消 SKC ${row.skc || ''} 的报名？提交后将在后台异步执行，页面会显示进度；其它记录可继续取消。`,
      confirmText: '确认取消',
      cancelText: '返回',
    });
    if (!ok) return;
    // 不 await：允许多个取消任务并行
    startCancelJob([row]);
  }, [startCancelJob]);

  /** 仅删本地缓存：不调用平台取消接口 */
  const deleteEnrollsLocal = useCallback(async (partakeGoodsIds) => {
    const ids = (Array.isArray(partakeGoodsIds) ? partakeGoodsIds : [])
      .filter((v) => v != null && v !== '');
    if (!ids.length) return 0;
    const res = await sheinPlatformAPI.deleteMarketingEnrolls(shopId, {
      partakeGoodsIdList: ids.map((v) => (Number.isFinite(Number(v)) ? Number(v) : v)),
    });
    const deleted = Number(res?.deleted ?? res?.data?.deleted);
    return Number.isFinite(deleted) ? deleted : ids.length;
  }, [shopId]);

  const deleteEnroll = useCallback(async (row, key) => {
    const partakeGoodsId = row?.partakeGoodsId;
    if (partakeGoodsId == null || partakeGoodsId === '') {
      showToast(fixedUi("报名记录 ID 无效"), 'warning');
      return;
    }
    const ok = await showSheinConfirm({
      title: '删除报名记录',
      message: `确认删除 SKC ${row.skc || ''} 的本地报名记录？仅删除本地缓存数据，不会取消平台报名，重新同步后可再次拉回。`,
      tone: 'danger',
      confirmText: '删除',
      cancelText: '返回',
    });
    if (!ok) return;
    setDeletingId(String(partakeGoodsId));
    try {
      await deleteEnrollsLocal([partakeGoodsId]);
      showToast(fixedUi("已删除本地记录"), 'success');
      if (key) dropSelection([key]);
      await loadList(shopId, applied, pageView, page, pageSize);
    } catch (e) {
      showToast(e?.message || '删除失败', 'error');
    } finally {
      setDeletingId('');
    }
  }, [shopId, applied, pageView, page, pageSize, loadList, deleteEnrollsLocal, dropSelection]);

  /** 批量取消：仅审核中的报名可取消；已在异步任务中的自动跳过 */
  const batchCancelEnrolls = async () => {
    const selected = resolveSelectedRows();
    const cancellable = selected.filter((row) => canCancelEnroll(row));
    if (!selected.length) {
      showToast(fixedUi("请先选择报名记录"), 'warning');
      return;
    }
    if (!cancellable.length) {
      showToast(fixedUi("所选记录中没有可取消的报名（仅审核中可取消）"), 'warning');
      return;
    }
    const targets = cancellable.filter((row) => {
      const id = row?.partakeGoodsId == null ? '' : String(row.partakeGoodsId);
      return id && !pendingCancelIds.has(id);
    });
    const skippedStatus = selected.length - cancellable.length;
    const skippedBusy = cancellable.length - targets.length;
    if (!targets.length) {
      showToast(fixedUi("所选可取消记录均已在取消任务中，请等待完成后再试"), 'warning');
      return;
    }
    const skipHints = [
      skippedStatus > 0 ? `${skippedStatus} 条非「审核中」` : '',
      skippedBusy > 0 ? `${skippedBusy} 条进行中` : '',
    ].filter(Boolean).join('、');
    const ok = await showSheinConfirm({
      title: '批量取消报名',
      message: `确认取消 ${targets.length} 条报名？提交后将在后台异步执行；其它记录可继续取消。${skipHints ? `另有 ${skipHints} 将自动跳过。` : ''}`,
      confirmText: '批量取消',
      cancelText: '返回',
    });
    if (!ok) return;
    startCancelJob(targets, { clearSelection: true });
  };

  /** 批量删除：只删本地缓存，不调用平台取消接口 */
  const batchDeleteEnrolls = async () => {
    const selected = resolveSelectedRows();
    if (!selected.length) {
      showToast(fixedUi("请先选择报名记录"), 'warning');
      return;
    }
    const ok = await showSheinConfirm({
      title: '批量删除报名记录',
      message: `确认删除选中的 ${selected.length} 条本地报名记录？仅删除本地缓存数据，不会取消平台报名，重新同步后可再次拉回。`,
      tone: 'danger',
      confirmText: '批量删除',
      cancelText: '返回',
    });
    if (!ok) return;
    setBatchDeleting(true);
    try {
      const deleted = await deleteEnrollsLocal(selected.map((row) => row.partakeGoodsId));
      showToast(`已删除本地记录 ${deleted} 条`, 'success');
      await loadList(shopId, applied, pageView, page, pageSize);
      setSelectedKeys(new Set());
      setSelectedRowsMap({});
    } catch (e) {
      showToast(e?.message || '批量删除失败', 'error');
    } finally {
      setBatchDeleting(false);
    }
  };

  const batchOpsMenu = [
    {
      key: 'batch-cancel',
      label: t('marketing.batchCancel'),
      count: selectedKeys.size,
      disabled: batchDeleting,
      onClick: batchCancelEnrolls,
    },
    {
      key: 'batch-delete',
      label: t('returnList.batchDelete'),
      count: selectedKeys.size,
      tone: 'danger',
      disabled: batchDeleting,
      onClick: batchDeleteEnrolls,
    },
  ];

  const typeTabs = useMemo(() => (
    <div className="spo-status-tabs" role="tablist" aria-label={t('marketing.typeAria')}>
      {ACTIVITY_TYPE_TABS.map((tab) => {
        const countKey = tab.value === '' ? 'all' : tab.value;
        const count = Number(typeCounts?.[countKey] ?? 0);
        const active = String(applied.activityType || '') === String(tab.value);
        return (
          <button
            key={tab.value || 'all'}
            type="button"
            role="tab"
            aria-selected={active}
            className={`spo-status-tab${active ? ' spo-status-tab--active' : ''}`}
            onClick={() => onActivityTypeTab(tab.value)}
          >
            {tab.labelKey ? t(tab.labelKey) : tab.label}
            <span className="spo-status-tab__count">{Number.isFinite(count) ? count : 0}</span>
          </button>
        );
      })}
    </div>
  ), [applied.activityType, typeCounts, t]);

  const auditStatusTabs = useMemo(() => (
    <div className="spo-status-tabs" role="tablist" aria-label={fixedUi("审核状态")}>
      {AUDIT_STATUS_TABS.map((tab) => {
        const countKey = tab.value === '' ? 'all' : tab.value;
        const count = Number(statusCounts?.[countKey] ?? 0);
        const active = String(applied.auditStatus || '') === String(tab.value);
        return (
          <button
            key={tab.value || 'all-audit'}
            type="button"
            role="tab"
            aria-selected={active}
            className={`spo-status-tab${active ? ' spo-status-tab--active' : ''}`}
            onClick={() => onAuditStatusTab(tab.value)}
          >
            {tab.labelKey ? t(tab.labelKey) : tab.label}
            <span className="spo-status-tab__count">{Number.isFinite(count) ? count : 0}</span>
          </button>
        );
      })}
    </div>
  ), [applied.auditStatus, statusCounts, t]);

  const listTabs = useMemo(
    () => stackShopDimensionTabs(
      shopDimensionTabs,
      pageView === 'enrolls' ? auditStatusTabs : typeTabs,
    ),
    [shopDimensionTabs, pageView, typeTabs, auditStatusTabs],
  );

  const emptyTitle = !shopId
    ? t('returnList.pickShop')
    : (loading
      ? t('returnList.loading')
      : (neverSynced
        ? t(meta.emptyNeverKey)
        : (rows.length === 0 ? t(meta.emptyNoneKey) : t('freightLib.noData'))));

  const emptyHint = useMemo(() => {
    if (!shopId || loading || rows.length > 0) return '';
    if (neverSynced) {
      if (pageView === 'enrolls') {
        return activitiesSynced
          ? fixedUi('点击工具栏右侧「拉取 SHEIN 后台数据」获取平台报名记录。')
          : fixedUi('请先到「活动列表」点击「拉取 SHEIN 后台数据」获取可报名活动，再回到本页点「拉取 SHEIN 后台数据」获取报名记录。');
      }
      return fixedUi('点击工具栏右侧「拉取 SHEIN 后台数据」获取平台可报名活动。');
    }
    const hasFilter = Boolean(applied.keyword || applied.activityType || applied.auditStatus);
    if (hasFilter) return t('marketing.adjustFilter');
    return '';
  }, [shopId, loading, rows.length, neverSynced, applied, pageView, activitiesSynced, t]);

  /** 报名记录页：活动列表从未同步过时提示先拉取活动列表（只提示，不拦截同步） */
  const showActivitiesSyncNotice = pageView === 'enrolls'
    && Boolean(shopId) && !loading && rows.length > 0 && !activitiesSynced;

  const renderCheckTh = () => (
    <th
      scope="col"
      className="spo-ops-th spo-ops-th--check shein-product-cell--spu-check"
      onClick={() => {
        if (!shopId || loading || selectableKeys.length === 0) return;
        toggleAll();
      }}
    >
      <input
        type="checkbox"
        aria-label={t(meta.selectAllLabelKey)}
        disabled={!shopId || loading || selectableKeys.length === 0}
        checked={allSelected}
        onClick={(e) => e.stopPropagation()}
        onChange={toggleAll}
      />
    </th>
  );

  const renderCheckTd = (key, row) => (
    <td
      className="spo-ops-td spo-ops-td--check shein-product-cell shein-product-cell--spu-check"
      onClick={() => toggleOne(key, row)}
    >
      <input
        type="checkbox"
        aria-label={`选择 ${key}`}
        checked={selectedKeys.has(key)}
        onClick={(e) => e.stopPropagation()}
        onChange={() => toggleOne(key, row)}
      />
    </td>
  );

  const activeCancelJobs = useMemo(
    () => Object.values(cancelJobs).filter(Boolean),
    [cancelJobs],
  );

  return (
    <>
      <SheinSyncProgressSlot kinds={meta.syncKinds} />
      {pageView === 'enrolls' && activeCancelJobs.length > 0 ? (
        <div className="spo-enroll-job">
          {activeCancelJobs.map((job) => (
            <SheinSyncProgressBar
              key={job.jobId}
              progress={{
                visible: true,
                status: job.status || 'running',
                percent: job.percent,
                message: job.message,
                label: t('marketing.cancelEnroll'),
              }}
            />
          ))}
          <p className="spo-enroll-job__hint">
            {activeCancelJobs.length > 1
              ? `当前有 ${activeCancelJobs.length} 个取消任务在后台执行；已锁定任务中的记录，其它记录可继续取消。`
              : '取消任务已在后台异步执行；已锁定本任务记录，其它记录可继续取消。完成后会提示结果并刷新列表。'}
          </p>
        </div>
      ) : null}
      <ProductFilterCard
        toolbar={(
          <SheinOpsListToolbar
            selectedCount={selectedKeys.size}
            busy={loading || exporting || syncing || Boolean(deletingId) || batchDeleting}
            batchOpsMenu={pageView === 'enrolls' ? batchOpsMenu : []}
            batchOpsLabel="批量操作"
            batchCopying={activeCancelJobs.length > 0}
            batchCopyingLabel={
              activeCancelJobs.length > 1
                ? `后台取消中（${activeCancelJobs.length}）…`
                : '正在后台取消…'
            }
            batchMoving={batchDeleting}
            batchMovingLabel="正在批量删除…"
            syncKinds={meta.syncKinds}
            quotaKind={meta.quotaKind}
            onSync={onSync}
            syncing={syncing || loading}
            syncCooldownSeconds={syncCooldown}
            syncDisabled={!shopId || syncing || loading}
            lastSyncedLabel={lastSyncedLabel}
            syncHint={t(meta.syncHintKey)}
            exportLabel={opsExportLabel(exporting)}
            exportDisabled={!shopId || exporting || total === 0}
            exportMenu={buildOpsExportMenu({
              exporting,
              filteredCount: total,
              pageCount: rows.length,
              selectedCount: selectedKeys.size,
              onExportFiltered: exportFiltered,
              onExportPage: () => exportRows(rows),
              onExportSelected: () => exportRows(resolveSelectedRows()),
            })}
          />
        )}
      >
        <div className="spo-filter-bar">
          <div className="spo-filter-bar__fields">
            <div className="spo-field spo-field--inline spo-field--w300">
              <span className="spo-field__label">{fixedUi("关键词")}</span>
              <input
                className="spo-input"
                type="search"
                value={filterDraft.keyword}
                disabled={!shopId}
                placeholder={t(meta.keywordPlaceholderKey)}
                onChange={(e) => setFilterDraft((prev) => ({ ...prev, keyword: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
            </div>
          </div>
          <div className="spo-filter-bar__actions">
            <button type="button" className="spo-btn spo-btn--secondary spo-btn--sm" disabled={!shopId} onClick={onReset}>
              {fixedUi("重置")}
            </button>
            <button
              type="button"
              className="spo-btn spo-btn--primary spo-btn--sm"
              disabled={!shopId || loading}
              onClick={onQuery}
            >
              {fixedUi("查询")}
            </button>
          </div>
        </div>
        {error ? <div className="spo-ops-error" role="alert">{error}</div> : null}
        {truncated ? (
          <p className="spo-empty-hint" role="status">
            {t('marketing.truncated')}
          </p>
        ) : null}
        {showActivitiesSyncNotice ? (
          <p className="spo-ops-notice" role="status">
            {t('marketing.sheinNeedAct')}
          </p>
        ) : null}
      </ProductFilterCard>

      <ProductListCard
        tabs={listTabs}
        loading={Boolean(shopId) && loading}
        loadingLabel={t(meta.loadingLabelKey)}
      >
        {pageView === 'enrolls' ? (
          <div className="spo-drafts__table-wrap spo-ops-table-wrap">
            <table className="spo-publish__table spo-drafts__table spo-ops-table spo-ops-table--sticky-head spo-marketing-enrolls-table">
              <colgroup>
                <col className="spo-ops-col spo-ops-col--check shein-product-col--check" />
                <col className="spo-marketing-col--info" />
                <col className="spo-marketing-col--skc" />
                <col className="spo-marketing-col--activity" />
                <col className="spo-marketing-col--audit" />
                <col className="spo-marketing-col--enroll-info" />
                <col className="spo-marketing-col--sku" />
                <col className="spo-marketing-col--price" />
                <col className="spo-ops-col--ops" />
              </colgroup>
              <thead>
                <tr>
                  {renderCheckTh()}
                  <th scope="col">{fixedUi("产品信息")}</th>
                  <th scope="col">SKC</th>
                  <th scope="col">{fixedUi("活动 ID")}</th>
                  <th scope="col">{fixedUi("审核状态")}</th>
                  <th scope="col">{fixedUi("报名信息")}</th>
                  <th scope="col">SKU</th>
                  <th scope="col">{fixedUi("价格")}</th>
                  <th scope="col">{fixedUi("操作")}</th>
                </tr>
              </thead>
              <tbody>
                {!shopId || loading || rows.length === 0 ? (
                  <tr>
                    <td colSpan={meta.colSpan} className="spo-certs-empty-cell">
                      <div className="spo-certs-empty">
                        <p className="spo-certs-empty__title">{emptyTitle}</p>
                        {emptyHint ? <p className="spo-certs-empty__desc">{emptyHint}</p> : null}
                      </div>
                    </td>
                  </tr>
                ) : (
                  groupEnrollRowsBySpu(rows).map((group) => {
                    const groupSize = group.items.length;
                    return group.items.map(({ row, index }, rowIdx) => {
                      const isFirst = rowIdx === 0;
                      const key = rowKey(row, pageView, index);
                      const selected = selectedKeys.has(key);
                      const tone = auditStatusTone(row.auditStatus);
                      const cancelling = pendingCancelIds.has(String(row.partakeGoodsId));
                      const deleting = deletingId === String(row.partakeGoodsId);
                      const info = enrollProductInfo(row);
                      const skuRows = enrollSkuPriceRows(row);
                      const previewSkuRows = skuRows.slice(0, ENROLL_SKU_PREVIEW_LIMIT);
                      const hasMoreSkus = skuRows.length > previewSkuRows.length;
                      return (
                        <tr
                          key={key}
                          className={`spo-row--clickable spo-marketing-enroll-row${isFirst ? '' : ' spo-marketing-enroll-row--child'}${selected ? ' is-selected' : ''}`}
                          onClick={(e) => onRowClickSelect(e, key, row)}
                        >
                          <td className="spo-ops-td spo-ops-td--check shein-product-cell shein-product-cell--spu-check">
                            <input
                              type="checkbox"
                              aria-label={`选择 ${key}`}
                              checked={selected}
                              onClick={(e) => e.stopPropagation()}
                              onChange={() => toggleOne(key, row)}
                            />
                          </td>
                          {isFirst ? (
                            <td
                              rowSpan={groupSize}
                              className="shein-product-cell shein-product-cell--info spo-marketing-enroll-group"
                            >
                              <div className="shein-product-info shein-product-info--text-only spo-marketing-enroll-product">
                                <div className="shein-product-info__text">
                                  <HoverCopyField
                                    value={group.product.title || fixedUi('未命名商品')}
                                    className="shein-product-info__title"
                                    popoverCopy
                                  />
                                  <HoverCopyField
                                    label="SPU"
                                    value={group.product.spu}
                                    className="shein-product-info__meta"
                                    separator=": "
                                  />
                                  <HoverCopyField
                                    label={fixedUi("货号")}
                                    value={group.product.supplierCode}
                                    className="shein-product-info__meta"
                                    separator=": "
                                  />
                                  <div className="shein-product-info__meta">
                                    SKC 数: {groupSize}
                                  </div>
                                </div>
                              </div>
                            </td>
                          ) : null}
                          <td className="shein-product-cell shein-product-cell--spec">
                            <div className="shein-product-spec spo-marketing-enroll-skc">
                              <SheinProductThumb imageUrl={info.imageUrl} alt={info.skc || 'SKC'} />
                              <div className="shein-product-spec__text">
                                <div className="shein-product-spec__color">
                                  {info.color || '—'}
                                </div>
                                <HoverCopyField
                                  label="SKC"
                                  value={info.skc}
                                  className="shein-product-spec__skc"
                                  separator=": "
                                />
                              </div>
                            </div>
                          </td>
                          <td>
                            <HoverCopyField value={String(row.activityId ?? '')} />
                          </td>
                          <td className="spo-marketing-enroll-audit">
                            <span className={`spo-records__status spo-records__status--${tone === 'pass' ? 'success' : (tone === 'fail' ? 'failed' : 'pending')}`}>
                              {auditStatusLabel(row.auditStatus)}
                            </span>
                            {row.refuseReason ? (
                              <div className="spo-marketing-audit__reason" title={row.refuseReason}>
                                {row.refuseReason}
                              </div>
                            ) : null}
                          </td>
                          <td>
                            <div className="spo-marketing-enroll-info">
                              <div>
                                <span className="spo-marketing-enroll-info__label">{fixedUi("报名时间：")}</span>
                                <span>{formatMsTime(row.partakeTime)}</span>
                              </div>
                              <div>
                                <span className="spo-marketing-enroll-info__label">{fixedUi("活动件数：")}</span>
                                <span>{row.actStock != null ? row.actStock : '—'}</span>
                              </div>
                              <div>
                                <span className="spo-marketing-enroll-info__label">{fixedUi("预留件数：")}</span>
                                <span>{row.reserveStock != null ? row.reserveStock : '—'}</span>
                              </div>
                            </div>
                          </td>
                          <td className="shein-product-cell spo-marketing-enroll-sku">
                            <div className="shein-product-sku-preview">
                              {previewSkuRows.length ? (
                                <>
                                  {previewSkuRows.map((sku) => (
                                    <div key={sku.key} className="shein-product-sku-preview__item">
                                      <HoverCopyField
                                        value={sku.sku}
                                        className="shein-product-sku-preview__code"
                                      />
                                    </div>
                                  ))}
                                  {hasMoreSkus ? (
                                    <button
                                      type="button"
                                      className="shein-product-sku-preview__more"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openSkuModal(group, info, skuRows);
                                      }}
                                    >
                                      查看更多（{skuRows.length}）
                                    </button>
                                  ) : null}
                                </>
                              ) : (
                                <div className="shein-product-sku-preview__item">—</div>
                              )}
                            </div>
                          </td>
                          <td className="shein-product-cell spo-marketing-enroll-price">
                            <div className="shein-product-sku-preview">
                              {previewSkuRows.length ? (
                                <>
                                  {previewSkuRows.map((sku) => (
                                    <div key={sku.key} className="shein-product-sku-preview__item">
                                      <div className="shein-product-sku-preview__size">{sku.priceText}</div>
                                    </div>
                                  ))}
                                  {hasMoreSkus ? (
                                    <button
                                      type="button"
                                      className="shein-product-sku-preview__more"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openSkuModal(group, info, skuRows);
                                      }}
                                    >
                                      查看更多（{skuRows.length}）
                                    </button>
                                  ) : null}
                                </>
                              ) : (
                                <div className="shein-product-sku-preview__item">—</div>
                              )}
                            </div>
                          </td>
                          <td className="spo-marketing-enroll-ops">
                            <div className="spo-marketing-list__ops">
                              {canCancelEnroll(row) ? (
                                <button
                                  type="button"
                                  className="spo-btn spo-btn--ghost spo-btn--sm"
                                  disabled={cancelling}
                                  onClick={() => cancelEnroll(row)}
                                >
                                  {cancelling ? '取消中…' : fixedUi("取消")}
                                </button>
                              ) : (
                                /* 占位：不可取消时也占住「取消」按钮的完整盒模型，让「删除」纵向对齐成一列 */
                                <button
                                  type="button"
                                  className="spo-btn spo-btn--ghost spo-btn--sm spo-marketing-enroll-ops__placeholder"
                                  tabIndex={-1}
                                  aria-hidden="true"
                                >
                                  {fixedUi("取消")}
                                </button>
                              )}
                              <button
                                type="button"
                                className="spo-btn spo-btn--ghost spo-btn--sm spo-btn--danger-text"
                                disabled={deleting}
                                onClick={() => deleteEnroll(row, key)}
                              >
                                {deleting ? '删除中…' : fixedUi("删除")}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    });
                  })
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="spo-drafts__table-wrap spo-ops-table-wrap">
            <table className="spo-publish__table spo-drafts__table spo-ops-table spo-ops-table--sticky-head">
              <colgroup>
                <col className="spo-ops-col spo-ops-col--check shein-product-col--check" />
              </colgroup>
              <thead>
                <tr>
                  {renderCheckTh()}
                  <th scope="col">{fixedUi("活动名称")}</th>
                  <th scope="col">{fixedUi("类型")}</th>
                  <th scope="col">{fixedUi("活动 ID")}</th>
                  <th scope="col">{fixedUi("报名截止")}</th>
                  <th scope="col">{fixedUi("活动起止")}</th>
                  <th scope="col">{fixedUi("操作")}</th>
                </tr>
              </thead>
              <tbody>
                {!shopId || loading || rows.length === 0 ? (
                  <tr>
                    <td colSpan={meta.colSpan} className="spo-certs-empty-cell">
                      <div className="spo-certs-empty">
                        <p className="spo-certs-empty__title">{emptyTitle}</p>
                        {emptyHint ? <p className="spo-certs-empty__desc">{emptyHint}</p> : null}
                      </div>
                    </td>
                  </tr>
                ) : (
                  rows.map((row, index) => {
                    const key = rowKey(row, pageView, index);
                    const selected = selectedKeys.has(key);
                    return (
                      <tr
                        key={key}
                        className={`spo-row--clickable${selected ? ' is-selected' : ''}`}
                        onClick={(e) => onRowClickSelect(e, key, row)}
                      >
                        {renderCheckTd(key, row)}
                        <td title={row.activityName || ''}>{row.activityName || '—'}</td>
                        <td>{activityTypeLabel(row.activityType)}</td>
                        <td>
                          <HoverCopyField value={String(row.activityId ?? '')} />
                        </td>
                        <td>
                          {row.partakeEndTimeFormatted
                            || formatMsTime(row.partakeEndTime)
                            || '—'}
                        </td>
                        <td>
                          {formatTimeRange(row.activityStartTime, row.activityEndTime)}
                        </td>
                        <td>
                          <div className="spo-marketing-list__ops">
                            <button
                              type="button"
                              className="spo-btn spo-btn--ghost spo-btn--sm"
                              onClick={() => setDrawerActivity(row)}
                            >
                              {fixedUi("查看")}
                            </button>
                            <button
                              type="button"
                              className="spo-btn spo-btn--primary spo-btn--sm"
                              onClick={() => openEnrollInNewTab(row)}
                            >
                              {fixedUi("报名")}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
        <SheinOpsPager
          page={page}
          pageSize={pageSize}
          total={total}
          loading={loading}
          disabled={!shopId}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          ariaLabel={t(meta.pagerLabelKey)}
        />
      </ProductListCard>

      {pageView === 'activities' ? (
        <SheinMarketingActivityDrawer
          open={Boolean(drawerActivity)}
          shopId={shopId}
          activity={drawerActivity}
          onClose={() => setDrawerActivity(null)}
        />
      ) : null}

      {pageView === 'enrolls' ? (
        <SheinMarketingSkuPriceModal
          open={Boolean(skuModal)}
          spu={skuModal?.spu || ''}
          skc={skuModal?.skc || ''}
          supplierCode={skuModal?.supplierCode || ''}
          rows={skuModal?.rows || []}
          onClose={() => setSkuModal(null)}
        />
      ) : null}
    </>
  );
}
